import { Plus, Minus, Rows3, Columns3 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ChannelStrip from "./ChannelStrip";
import MixerRow from "./MixerRow";

export default function MixerPanel({
  channels, visibleChannels, selectedChannel, peaks, channelVolumes, engine, recordingIdx, fxCounts,
  activeEffects, onToggleFx, layout = "strips", onLayoutChange, onOpenClipEditor,
  stackWidth = 1, onStackWidthChange,
  onSelectChannel, onUpload, onClear, onFewer, onMore,
  armedChannels, onToggleArm, sourceMap, onSourceChChange, inputChannelCount = 0,
  onRecordChannel,
  onOpenVocalCleanup,
  channelLocks, collabConnected,
  // iter-105 — drives the BPM-synced row pulse on loaded channels.
  playing = false,
  // iter-198 — live waveform feed accessor + rev counter.
  readLiveSamples = null,
  isClipFlashing = null,
  isClipBadgeActive = null,
  liveWaveRev = 0,
  // iter-246 — per-channel take history.
  takeCounts = null,
  onOpenTakeHistory = null,
  // iter-265 — Current loop length (seconds). Drives the per-channel
  // "Fit to loop" affordance.
  loopLengthSec = 0,
}) {
  // Count stack-harmony channels — detect by name pattern "(N/M)".
  const stackedCount = channels.reduce((n, c) => {
    if (!c?.buffer || !c.name) return n;
    return /\(\d+\/\d+\)/.test(c.name) ? n + 1 : n;
  }, 0);
  return (
    <Card className="glass-card rounded-3xl border-0 text-white">
      <CardContent className="min-w-0 overflow-hidden p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">64 Channel Mixer</h2>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
              {layout === "rows"
                ? "Horizontal faders · click row to select · pencil icon to edit clip"
                : "Scroll horizontally · click strips to select · tap "}
              {layout !== "rows" && <span className="text-fuchsia-300">FX</span>}
              {layout !== "rows" && " badge for EQ · Comp · Reverb · Delay · Limiter · AutoTune"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Vocal Width — re-pans all stack-harmony channels in unison. */}
            {stackedCount >= 2 && (
              <div
                data-testid="mixer-vocal-width"
                className="flex items-center gap-2 rounded-2xl border border-cyan-400/30 bg-cyan-500/[0.06] px-2.5 py-1.5"
                title="Vocal Width — 0 = mono center · 1 = full stereo spread"
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-cyan-200">
                  Vocal Width
                </span>
                <input
                  data-testid="mixer-vocal-width-slider"
                  type="range" min="0" max="1" step="0.01"
                  value={stackWidth}
                  onChange={(e) => onStackWidthChange?.(Number(e.target.value))}
                  className="w-28 accent-cyan-400"
                />
                <span className="w-8 text-right font-mono text-[10px] text-cyan-100/80">
                  {Math.round(stackWidth * 100)}%
                </span>
              </div>
            )}
            {/* Layout toggle — left-to-right faders (rows) vs vertical strips */}
            <div className="flex items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.04] px-1.5 py-1">
              <Button
                data-testid="mixer-layout-rows"
                onClick={() => onLayoutChange?.("rows")}
                size="sm"
                variant="secondary"
                className={`h-7 rounded-xl px-2 text-[10px] uppercase tracking-wider ${
                  layout === "rows" ? "bg-fuchsia-500/30 text-fuchsia-100 ring-1 ring-fuchsia-400/40" : "bg-white/[0.06] text-white/65 hover:bg-white/[0.12]"
                }`}
                title="Horizontal-fader rows layout"
              >
                <Rows3 className="mr-1 h-3 w-3" />Rows
              </Button>
              <Button
                data-testid="mixer-layout-strips"
                onClick={() => onLayoutChange?.("strips")}
                size="sm"
                variant="secondary"
                className={`h-7 rounded-xl px-2 text-[10px] uppercase tracking-wider ${
                  layout === "strips" ? "bg-fuchsia-500/30 text-fuchsia-100 ring-1 ring-fuchsia-400/40" : "bg-white/[0.06] text-white/65 hover:bg-white/[0.12]"
                }`}
                title="Vertical mixer-strips layout"
              >
                <Columns3 className="mr-1 h-3 w-3" />Strips
              </Button>
            </div>
            <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-2 py-1">
              <Button data-testid="mixer-fewer" variant="secondary" className="h-8 rounded-xl border border-white/10 bg-white/[0.06] px-2 text-white hover:bg-white/[0.12]" onClick={onFewer}>
                <Minus className="h-4 w-4" />
              </Button>
              <span className="min-w-[88px] text-center font-mono text-xs text-white/70">{visibleChannels} / 64</span>
              <Button data-testid="mixer-more" variant="secondary" className="h-8 rounded-xl border border-white/10 bg-white/[0.06] px-2 text-white hover:bg-white/[0.12]" onClick={onMore}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        {layout === "rows" ? (
          /* `pl-1 -ml-1` matches the strips-layout inset (4px) so both layouts
             have pixel-identical left-edge breathing room — toggling layouts
             keeps the leftmost channel column in the exact same position. */
          <div className="max-h-[640px] overflow-y-auto pl-1 -ml-1 pr-1" data-testid="mixer-rows">
            {channels.slice(0, visibleChannels).map((c, i) => (
              <MixerRow
                key={`mixer-row-${i}`}
                index={i}
                channel={c}
                engine={engine}
                /* Primitive snapshots — needed so the memo's propsEqual can
                 * actually detect changes. The engine mutates `c.buffer/name/etc.`
                 * in place on the same object, so memo equality against the
                 * object reference is a false-positive (prev/next deref the
                 * same mutated value). Extract primitives at render time. */
                hasBuffer={!!c.buffer}
                channelName={c.name}
                /* iter-219 — primitive snapshot of `channel.sourceUrl` so
                   the cloud badge appears/disappears reactively. */
                channelSourceUrl={c.sourceUrl || null}
                channelMuted={!!c.muted}
                channelSolo={!!c.solo}
                /* iter-77 — color stripe for lineage-grouped channels.
                 * Primitive so propsEqual catches the group bind/unbind. */
                channelGroupColor={c.groupColor || null}
                /* Pitch bend + plugin count — primitive snapshots so the memo
                 * detects the change (engine mutates the underlying channel
                 * object in place). */
                pitchBendSemi={c.pitchBendSemi ?? 0}
                pluginCount={c.plugins?.length || 0}
                peak={peaks[i]}
                externalVolume={channelVolumes?.[i]}
                selected={selectedChannel === i}
                onSelect={onSelectChannel}
                onUpload={onUpload}
                onClear={onClear}
                recordingIdx={recordingIdx}
                onOpenClipEditor={onOpenClipEditor}
                armed={armedChannels?.has?.(i)}
                onToggleArm={onToggleArm}
                onRecordChannel={onRecordChannel}
                sourceCh={sourceMap?.[i] ?? 0}
                onSourceChChange={onSourceChChange}
                inputChannelCount={inputChannelCount}
                onOpenVocalCleanup={onOpenVocalCleanup}
                /* Phase-3 channel-lock state — falsy when not in a room. */
                lockedByMe={!!channelLocks?.isLockedByMe?.(i)}
                lockedByOther={!!channelLocks?.isLockedByOther?.(i)}
                lockHolder={channelLocks?.lockHolderInfo?.(i) || null}
                onToggleLock={collabConnected ? () => channelLocks?.toggleLock?.(i) : null}
                playing={playing}
                liveSamples={readLiveSamples ? (
                  recordingIdx === i ? readLiveSamples("rec", i) : readLiveSamples("play", i)
                ) : null}
                clipping={isClipFlashing ? isClipFlashing(i) : false}
                clippedRecently={isClipBadgeActive ? isClipBadgeActive(i) : false}
                liveWaveRev={liveWaveRev}
                takeCount={takeCounts ? (takeCounts[i] || 0) : 0}
                onOpenTakeHistory={onOpenTakeHistory}
              />
            ))}
          </div>
        ) : (
          /* Strips horizontal scroller. `pl-1 -ml-1 scroll-pl-1` together give
             the leftmost strip 4px of inset breathing room WITHOUT shifting
             the visual baseline of the panel header above it. `scroll-pl-1`
             snaps the leftmost strip to the same 4px offset after a
             programmatic scrollLeft = 0 reset so it can't sit flush against
             the panel's translucent left edge. */
          <div className="flex gap-3 overflow-x-auto pb-3 pl-1 -ml-1 scroll-pl-1" data-testid="mixer-scroll">
            {channels.slice(0, visibleChannels).map((c, i) => (
              <ChannelStrip
                key={`mixer-strip-${i}`}
                index={i}
                channel={c}
                engine={engine}
                peak={peaks[i]}
                externalVolume={channelVolumes?.[i]}
                selected={selectedChannel === i}
                onSelect={onSelectChannel}
                onUpload={onUpload}
                onClear={onClear}
                recordingIdx={recordingIdx}
                fxCount={fxCounts[i]}
                activeFx={activeEffects?.[i]}
                onToggleFx={onToggleFx}
                armed={armedChannels?.has?.(i)}
                onToggleArm={onToggleArm}
                onRecordChannel={onRecordChannel}
                /* Phase-3 channel-lock state — falsy when not in a room. */
                lockedByMe={!!channelLocks?.isLockedByMe?.(i)}
                lockedByOther={!!channelLocks?.isLockedByOther?.(i)}
                lockHolder={channelLocks?.lockHolderInfo?.(i) || null}
                onToggleLock={collabConnected ? () => channelLocks?.toggleLock?.(i) : null}
                liveSamples={readLiveSamples ? (
                  recordingIdx === i ? readLiveSamples("rec", i) : readLiveSamples("play", i)
                ) : null}
                clipping={isClipFlashing ? isClipFlashing(i) : false}
                clippedRecently={isClipBadgeActive ? isClipBadgeActive(i) : false}
                liveWaveRev={liveWaveRev}
                loopLengthSec={loopLengthSec}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
